import { generateUuid } from '../../../helpers/string';
import { TaxonResource } from '../../@api/Sylius';
import { CategorySliderTypeResource } from '../../@blocks/CategorySlider/CategorySlider';
import { transformToBlock } from '../../Block/BlockTransformers';
import { transformToImage } from '../../Media/MediaTransformers';
import { transformToSeo } from '../../Seo/SeoTransformers';
import { transformToProductListCallToAction } from '../ProductListCallToAction/ProductListCallToActionTransformers';
import {
    defaultCategoryPageData,
    ProductCategory,
    ProductCategoryBase,
    ProductCategoryImageType,
    ProductCategoryPageData,
    ProductCategoryResource,
} from './ProductCategory';

export const transformTaxonResourceToProductCategoryBase = (resource: TaxonResource): ProductCategoryBase => ({
    id: resource.code,
    slug: resource.slug || '',
});

export const transformTaxonResourceToProductCategoryBaseWithChildren = (resource: TaxonResource): ProductCategoryBase[] => {
    const base = transformTaxonResourceToProductCategoryBase(resource);
    const children = resource.children.flatMap(transformTaxonResourceToProductCategoryBaseWithChildren);

    return [base, ...children];
};

export const transformToProductCategoryBase = (resource: ProductCategoryResource): ProductCategoryBase => ({
    id: resource.sylius_id.toString(),
    slug: resource.slug,
});

export const transformToProductCategory = (
    resource: ProductCategoryResource,
    type: CategorySliderTypeResource,
): ProductCategory => {
    const defaultProductCategoryPageData = defaultCategoryPageData();
    const productCategoryBase = transformToProductCategoryBase(resource);

    const lineImage = resource.lineImage
        ? transformToImage(resource.lineImage)
        : undefined;

    const mainImage = resource.image
        ? transformToImage(resource.image)
        : defaultProductCategoryPageData.image;

    const isLineImage = (type === CategorySliderTypeResource.lineImage) && lineImage;

    const image = isLineImage
        ? lineImage
        : mainImage;

    const imageType = isLineImage
        ? ProductCategoryImageType.lineImage
        : ProductCategoryImageType.defaultImage;

    return {
        ...productCategoryBase,
        name: resource.title,
        image,
        imageType,
    };
};

export const transformTaxonToProductCategory = (resource: TaxonResource): ProductCategory => {
    const id = resource.id
        ? resource.id.toString()
        : generateUuid();

    const children = resource.children
        ? resource.children.map(transformTaxonToProductCategory)
        : [];

    const slug = resource.slug || resource.name?.toLowerCase() || '';

    return {
        id,
        slug,
        name: resource.name || '',
        children,
    };
};

export const transformToProductCategoryPageData = (resource: ProductCategoryResource): ProductCategoryPageData => {
    const defaultProductCategoryPageData = defaultCategoryPageData();

    const image = resource.image
        ? transformToImage(resource.image)
        : defaultProductCategoryPageData.image;

    const callToActions = resource.callToAction
        ? resource.callToAction.map(transformToProductListCallToAction)
        : defaultProductCategoryPageData.callToActions;

    const blocksBelowHeader = resource.elements
        ? resource.elements.map(transformToBlock)
        : defaultProductCategoryPageData.blocksBelowHeader;

    const blocksBelowProductList = resource.elementsBottom
        ? resource.elementsBottom.map(transformToBlock)
        : defaultProductCategoryPageData.blocksBelowProductList;

    const publishedBlocksBelowHeader = blocksBelowHeader.filter(block => block.isPublished);
    const publishedBlocksBelowProductList = blocksBelowProductList.filter(block => block.isPublished);

    const seo = resource.seo
        ? transformToSeo(resource.seo)
        : undefined;

    return {
        id: resource.sylius_id.toString(),
        title: resource.title,
        slug: resource.slug,
        meilisearchProductListName: resource.title,
        image,
        callToActions,
        blocksBelowHeader: publishedBlocksBelowHeader,
        blocksBelowProductList: publishedBlocksBelowProductList,
        seo,
    };
};

export const transformProductCategoryToNamesArray = (resource: ProductCategory): string[] => {
    const childNames = resource.children
        ? resource.children.flatMap(transformProductCategoryToNamesArray)
        : [];

    return [resource.name, ...childNames];
};
